import React, { useEffect, useMemo, useState } from 'react';
import { Dropdown, Radio } from 'tea-component';
import type { SingleCommonType } from '../../types';
import { commonBackInfo } from '../../utils';
import { useIntl } from 'umi';
import { TipsMapType, reviewerFilterTips } from '../../../BubbleCommon';
import SearchSelectedElement from '../SearchSelectedElement';
import { has } from 'lodash-es';
import { getPlaceholderText } from '../../constants/placeholder';
import { useSetSelectDefaultValue } from '../../hooks/init';
import { addFilterBuriedPoint } from '../../utils/point';
import { AllFilterPointType } from '@op/operationLog/pages/filter';
import { useInputFocus } from '../../hooks/common';
import './index.scss';

/**
 * 这种组件一般搜索选择，包含和排除
 * @param changeBasicOptions  修改基础选项
 * @param clearOptions  清理所有的选项
 * @param loading 搜索选择loading
 * @param setLoading  搜索选择loading
 * @param basicOptions 搜索选择基础的选项
 * @param getFilterList 获取筛选列表的函数
 * @param includeKey  搜索选择包含的key
 * @param excludeKey  搜索选择排除的key
 * @param resultKey   返回给外面结果的key
 * @param placeholderKey 占位符的key
 * @returns
 */
export default function IncludeExcludeSelected({
  onChange,
  defaultValue,
  filterValue,
  loading,
  setLoading,
  basicOptions,
  getFilterList,
  includeKey,
  excludeKey,
  resultKey,
  placeholderKey,
  changeBasicOptions,
  clearOptions,
  loadingText = 'The options provided by this filter are all lists of reviewers within the selected time period. When the selected time period is long, you may need to wait a while for the complete list of publishers to load.',
  placeholderText = 'Separate Multiple Reviewers With ",".',
  topTips = 'Please search and select the reviewer',
  mainText = 'Reviewer',
  allFilterTips = reviewerFilterTips,
  maxShowNum = 3,
  pointKey,
  returnType = 'text',
  hasAllBtn,
  className,
  hasBubble,
}: SingleCommonType & {
  loading: boolean;
  setLoading: (loading: boolean) => void;
  basicOptions: Array<any>;
  getFilterList: () => void;
  includeKey: string;
  excludeKey: string;
  resultKey: 'reviewer' | 'steamId' | 'urlAnalysis';
  changeBasicOptions: (values: Array<string>) => void;
  clearOptions: () => void;
  loadingText?: string;
  placeholderText?: string;
  topTips?: string;
  maxShowNum?: number;
  allFilterTips?: TipsMapType | null;
  pointKey?: AllFilterPointType;
  returnType?: 'text' | 'value';
  mainText?: string;
  hasAllBtn?: boolean;
  className?: string;
  placeholderKey: 'reviewers' | 'steamIds' | 'urlAnalysis';
  hasBubble?: boolean;
}) {
  const { formatMessage } = useIntl();
  const [type, setType] = useState('include');

  const inputProps = useInputFocus();

  function dealwithValue(valueObj: any = {}, updateType: boolean = false) {
    const isExclude = has(valueObj || {}, excludeKey);

    if (isExclude) {
      updateType && setType('exclude');
      return {
        ...valueObj,
        [includeKey]: valueObj[excludeKey],
      };
    }

    updateType && setType('include');
    return valueObj;
  }

  const realDefaultValue = useMemo(() => {
    return dealwithValue(defaultValue, true);
  }, [defaultValue]);

  const realFilterValue = useMemo(() => {
    return dealwithValue(
      filterValue,
      has(filterValue, excludeKey) || has(filterValue, includeKey),
    );
  }, [filterValue]);

  const {
    selects: values,
    setSelects: setValues,
    diffClassName: diffClassNameValue,
    isFirst,
  } = useSetSelectDefaultValue<string>({
    defaultValue: realDefaultValue,
    filterValue: realFilterValue,
    defaultKey: includeKey,
    options: [],
  });

  const getButtonElement = () => {
    const len = values.length;
    let text: string | number = values.join(', ');
    if (len === 0) {
      text = formatMessage({ id: 'All' });
    }
    if (len > maxShowNum) text = len;

    return text;
  };

  const onOpenChange = (value: boolean) => {
    if (value) {
      values.length === 0 ? getFilterList() : changeBasicOptions(values);
    } else {
      clearOptions();
    }

    inputProps.onFocus();
  };

  function getButtonElementInfo() {
    return (
      <div className="dbo-include-exclude-select-input">
        <div className="dbo-include-exclude-select-text">
          <span className="dbo-include-exclude-select-label">
            {getPlaceholderText(placeholderKey)}(
            {formatMessage({
              id: type === 'include' ? 'Include' : 'Exclude',
            })}
            )
          </span>
          {getButtonElement()}
        </div>
      </div>
    );
  }

  function getBackData() {
    const text = getButtonElement();
    let valueKey = excludeKey;
    let deleteKey = includeKey;

    if (type === 'include') {
      valueKey = includeKey;
      deleteKey = excludeKey;
    }

    return {
      data: { [valueKey]: values },
      fixedHeader: { [valueKey]: returnType === 'text' ? text : values },
      deleteKey,
    };
  }

  useEffect(() => {
    const { data, fixedHeader, deleteKey } = getBackData();

    onChange?.(resultKey, commonBackInfo(data, fixedHeader), [deleteKey]);
  }, [values, formatMessage, type]);

  const diffClassName = useMemo(() => {
    if (diffClassNameValue || type !== 'include') {
      return 'dbo-diff-filter';
    }
  }, [diffClassNameValue, type]);

  return (
    <div className={`dbo-include-exclude-select ${diffClassName} ${className}`}>
      <Dropdown
        placement="auto-start"
        overlayClassName="dbo-include-exclude-select-overlay"
        boxClassName={`dbo-include-exclude-select-box ${className}`}
        clickClose={false}
        onOpenChange={onOpenChange}
        button={getButtonElementInfo()}
        onClose={() => {
          try {
            const { data } = getBackData();
            addFilterBuriedPoint(pointKey, data);
          } catch (error) {}
        }}
        onOpen={() => {
          inputProps.onFocus();
        }}
      >
        <div className="dbo-radio-box">
          <Radio.Group value={type} onChange={(value) => setType(value)}>
            <Radio name="include">{formatMessage({ id: 'Include' })}</Radio>
            <Radio name="exclude">{formatMessage({ id: 'Exclude' })}</Radio>
          </Radio.Group>
        </div>
        <SearchSelectedElement
          tagList={values}
          loading={loading}
          setLoading={setLoading}
          basicOptions={basicOptions}
          setTagList={setValues}
          allFilterTips={allFilterTips}
          getFilterList={getFilterList}
          loadingText={loadingText}
          topTips={topTips}
          placeholderText={placeholderText}
          mainText={mainText}
          hasAllBtn={hasAllBtn}
          hasBubble={hasBubble}
          {...inputProps}
        ></SearchSelectedElement>
      </Dropdown>
    </div>
  );
}
